import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, RotateCcw, Loader2, Target, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import type { AnswerPayload, QuestionType } from '../../shared/protocol';
import { MultipleChoiceQuestion } from './questions/MultipleChoiceQuestion';
import { TrueFalseQuestion } from './questions/TrueFalseQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { FillBlankQuestion } from './questions/FillBlankQuestion';
import { MatchPairsQuestion, type PairLink } from './questions/MatchPairsQuestion';

interface StudyQuestion {
  id: string;
  index: number;
  type: QuestionType;
  title: string;
  mediaUrl?: string;
  timeLimit: number;
  pointsMultiplier: number;
  options?: { id: string; text: string }[];
  leftItems?: { id: string; text: string }[];
  rightItems?: { id: string; text: string }[];
}

interface CheckResult {
  isCorrect: boolean;
  correctness: number;
  explanation?: string;
  correctOptionIds?: string[];
  acceptedAnswers?: string[];
  correctPairs?: PairLink[];
}

/**
 * Modo estudio: el mismo contenido, a solas y sin reloj.
 *
 * No hay cronómetro ni puntuación, a propósito. Repasar y competir son
 * actividades distintas: la prisa ayuda a la segunda y estorba a la primera.
 * Tampoco reparte Dahus ni mueve el D-Rating, porque aquí puedes reintentar
 * cuantas veces quieras.
 */
export const AsyncStudyMode: React.FC<{ quizId: string; onExit: () => void }> = ({
  quizId,
  onExit,
}) => {
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState<StudyQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [index, setIndex] = useState(0);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // Respuesta en curso
  const [choice, setChoice] = useState<string | undefined>();
  const [multi, setMulti] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [links, setLinks] = useState<PairLink[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ title: string; questions: StudyQuestion[] }>(
          `/study/${quizId}`,
        );
        setTitle(data.title);
        setQuestions(data.questions);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo cargar el quiz.');
      } finally {
        setLoading(false);
      }
    })();
  }, [quizId]);

  const question = questions[index];
  const finished = index >= questions.length;

  const reset = () => {
    setChoice(undefined);
    setMulti([]);
    setText('');
    setLinks([]);
    setResult(null);
  };

  const check = async (payload: AnswerPayload) => {
    if (!question || result) return;
    setChecking(true);
    try {
      // La corrección la hace el servidor, igual que en las partidas: las
      // respuestas correctas no llegan al navegador antes de contestar.
      const data = await api.post<CheckResult>(`/study/${quizId}/check`, {
        questionId: question.id,
        payload,
      });
      setResult(data);
      setScore((s) => ({ correct: s.correct + (data.isCorrect ? 1 : 0), answered: s.answered + 1 }));
    } catch {
      setError('No se pudo comprobar la respuesta.');
    } finally {
      setChecking(false);
    }
  };

  const next = () => {
    reset();
    setIndex((i) => i + 1);
  };

  const restart = () => {
    reset();
    setIndex(0);
    setScore({ correct: 0, answered: 0 });
  };

  const accuracy = useMemo(
    () => (score.answered > 0 ? Math.round((score.correct / score.answered) * 100) : 0),
    [score],
  );

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto py-16 text-center space-y-4">
        <p className="text-danger-400">{error}</p>
        <button onClick={onExit} className="text-sky-400 hover:text-sky-300">
          Volver
        </button>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-6">
        <Target className="w-12 h-12 mx-auto text-sky-400" />
        <div>
          <h2 className="text-3xl font-display font-bold">Repaso terminado</h2>
          <p className="text-ink-400 mt-2">{title}</p>
        </div>
        <div className="surface-raised p-6">
          <p className="text-5xl font-display font-black text-brand-gradient">{accuracy}%</p>
          <p className="text-ink-400 mt-1">
            {score.correct} de {score.answered} correctas
          </p>
        </div>
        <div className="flex gap-3 justify-center">
          <button
            onClick={onExit}
            className="px-5 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors"
          >
            Salir
          </button>
          <button
            onClick={restart}
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Repetir
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="no-select-game max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Cabecera */}
      <div className="flex items-center gap-3">
        <button
          onClick={onExit}
          className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800 transition-colors"
          aria-label="Salir del repaso"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-ink-500 text-xs flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" />
            Modo estudio · sin reloj ni puntos
          </p>
          <h1 className="font-bold truncate">{title}</h1>
        </div>
        <span className="chip border-ink-700 bg-ink-850 text-ink-300">
          {index + 1} / {questions.length}
        </span>
      </div>

      {/* Progreso */}
      <div className="h-1.5 rounded-full bg-ink-800 overflow-hidden">
        <div
          className="h-full bg-brand-gradient transition-[width] duration-500"
          style={{ width: `${(index / questions.length) * 100}%` }}
        />
      </div>

      {/* Enunciado */}
      <h2 className="text-2xl md:text-3xl font-bold text-center text-balance leading-tight">
        {question.title}
      </h2>
      {question.mediaUrl && (
        <img
          src={question.mediaUrl}
          alt=""
          className="max-h-56 mx-auto rounded-2xl border border-ink-700 object-contain"
        />
      )}

      {/* Respuesta */}
      {question.type === 'multiple_choice' && (
        <MultipleChoiceQuestion
          options={question.options ?? []}
          selectedOptionId={choice}
          disabled={Boolean(result) || checking}
          correctOptionIds={result?.correctOptionIds}
          onSelect={(id) => {
            setChoice(id);
            void check({ kind: 'choice', optionId: id });
          }}
        />
      )}

      {question.type === 'true_false' && (
        <TrueFalseQuestion
          options={question.options ?? []}
          selectedOptionId={choice}
          disabled={Boolean(result) || checking}
          correctOptionIds={result?.correctOptionIds}
          onSelect={(id) => {
            setChoice(id);
            void check({ kind: 'choice', optionId: id });
          }}
        />
      )}

      {question.type === 'multi_select' && (
        <MultiSelectQuestion
          options={question.options ?? []}
          selectedIds={multi}
          submitted={Boolean(result)}
          disabled={Boolean(result)}
          correctOptionIds={result?.correctOptionIds}
          onToggle={(id) =>
            setMulti((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
          }
          onSubmit={() => void check({ kind: 'multi', optionIds: multi })}
        />
      )}

      {question.type === 'fill_blank' && (
        <FillBlankQuestion
          value={text}
          onChange={setText}
          submitted={Boolean(result)}
          disabled={Boolean(result)}
          acceptedAnswers={result?.acceptedAnswers}
          isCorrect={result?.isCorrect}
          onSubmit={() => void check({ kind: 'text', text })}
        />
      )}

      {question.type === 'match_pairs' && (
        <MatchPairsQuestion
          leftItems={question.leftItems ?? []}
          rightItems={question.rightItems ?? []}
          links={links}
          submitted={Boolean(result)}
          disabled={Boolean(result)}
          correctPairs={result?.correctPairs}
          onLink={(leftId, rightId) =>
            setLinks((prev) => [...prev.filter((l) => l.leftId !== leftId), { leftId, rightId }])
          }
          onUnlink={(leftId) => setLinks((prev) => prev.filter((l) => l.leftId !== leftId))}
          onSubmit={() => void check({ kind: 'pairs', links })}
        />
      )}

      {/* Explicación */}
      {result?.explanation && (
        <div className="surface p-5 animate-rise">
          <p className="text-ink-400 text-xs uppercase tracking-wider mb-1.5">Por qué</p>
          <p className="text-ink-100 leading-relaxed">{result.explanation}</p>
        </div>
      )}

      {/* Avance */}
      {result && (
        <div className="flex justify-center">
          <button
            onClick={next}
            className="flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
          >
            {index + 1 >= questions.length ? 'Ver resultado' : 'Siguiente'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};
